<template>
  <div class="min-h-screen flex flex-col items-center justify-center bg-white px-4">
    <!-- Logo + wordmark -->
    <div class="flex flex-col items-center mb-8 select-none">
      <img src="@/assets/args-parrot.svg" alt="Args OWI logo" class="h-32 mb-3" />
      <!-- <img src="@/assets/parrot_reloaded_transparent.png" alt="Args OWI logo" class="h-32 mb-3" /> -->
      <!-- <img src="@/assets/parrot_pirate_transparent.png" alt="Args OWI logo" class="h-48 mb-3" />  -->
      <h1 class="text-5xl font-light tracking-tight text-gray-800">
        args<span class="font-semibold text-blue-600">.</span>owi
      </h1>
      <p class="mt-2 text-sm text-gray-400 tracking-wide">Explore arguments for any claim</p>
    </div>

    <!-- Search bar -->
    <div class="w-full max-w-xl relative" ref="containerRef">
      <div class="flex items-center gap-2 rounded-full border border-gray-300 px-5 py-3 shadow-sm hover:shadow-md focus-within:shadow-md transition-shadow bg-white">
        <!-- magnifier icon -->
        <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          v-model="query"
          placeholder="Choose a claim…"
          class="flex-1 text-base outline-none bg-transparent text-gray-800 placeholder-gray-400"
          @focus="fetchSuggestions('', true)"
          @keydown.down.prevent="highlightNext"
          @keydown.up.prevent="highlightPrev"
          @keydown.enter.prevent="onEnter"
          @keydown.esc="suggestions = []"
          autocomplete="off"
          spellcheck="false"
        />
        <svg v-if="loading" class="w-4 h-4 text-gray-300 animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
        </svg>
      </div>

      <!-- Suggestions dropdown -->
      <ul
        v-if="suggestions.length"
        class="absolute z-50 mt-2 w-full rounded-2xl border border-gray-100 bg-white shadow-xl overflow-hidden"
        role="listbox"
      >
        <li
          v-for="(s, i) in suggestions"
          :key="s.claim_id"
          role="option"
          :aria-selected="i === highlighted"
          @mousedown.prevent="select(s)"
          @mouseenter="highlighted = i"
          class="px-5 py-3 cursor-pointer text-sm text-gray-800 truncate transition-colors"
          :class="i === highlighted ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'"
        >
          {{ s.text }}
        </li>
      </ul>
    </div>

    <p class="mt-5 text-xs text-gray-300">Select a claim from the suggestions to explore its arguments</p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import debounce from 'lodash.debounce'
import type { ClaimSearchHit, SearchResponse } from '@/types/search'

const router = useRouter()

const containerRef = ref<HTMLElement | null>(null)
const query = ref('')
const suggestions = ref<ClaimSearchHit[]>([])
const highlighted = ref<number | null>(null)
const loading = ref(false)

const fetchSuggestions = debounce(async (q: string, ignoreMinChars = false) => {
  if (!ignoreMinChars && q.length < 1) { suggestions.value = []; return }
  loading.value = true
  try {
    const API_BASE = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '')
    const url = new URL(`${API_BASE}/query-autocomplete`, window.location.origin)
    url.searchParams.set('q', q)
    const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data: SearchResponse = await res.json()
    suggestions.value = data.hits
    highlighted.value = null
  } catch {
    suggestions.value = []
  } finally {
    loading.value = false
  }
}, 250)

watch(query, (q) => fetchSuggestions(q))

const highlightNext = () => {
  if (!suggestions.value.length) return
  highlighted.value = highlighted.value === null ? 0 : Math.min(highlighted.value + 1, suggestions.value.length - 1)
}
const highlightPrev = () => {
  if (!suggestions.value.length) return
  highlighted.value = highlighted.value === null ? suggestions.value.length - 1 : Math.max(highlighted.value - 1, 0)
}
const onEnter = () => {
  const item = suggestions.value[highlighted.value ?? 0]
  if (item) select(item)
}

const onClickOutside = (e: MouseEvent) => {
  if (containerRef.value && !containerRef.value.contains(e.target as Node)) {
    suggestions.value = []
  }
}
onMounted(() => document.addEventListener('mousedown', onClickOutside))
onUnmounted(() => document.removeEventListener('mousedown', onClickOutside))

const select = (item: ClaimSearchHit) => {
  suggestions.value = []
  router.push({ path: '/', query: { claim_label: item.text } })
}
</script>
